import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Currency } from '@galaxy/billing';
import { PriceDisplayOverride } from '@galaxy/marketplace-apps';
import {
  calculateFeeAmount,
  galaxyFrequencyLabelToGalaxyFrequency,
  getFeesForEdition,
  getFrequencyValueForEdition,
  getLowestEditionId,
  getPriceValueForEdition,
  getSetupFeeForEdition,
  setInstanceAppPrice,
} from '@vendasta/billing-ui';
import { Frequency as GalaxyFrequency } from '@vendasta/galaxy/frequency';
import { Subscription } from 'rxjs';
import { PricePipe } from '../../../common/pipes/price.pipe';
import { PurchaseCost } from '../../../core/billing/purchase-cost';
import { ProductOrderFormStoreService } from '../../core/product-order-form.store.service';
import { updateOrderItemsWithCustomPrice } from '../../order-form-page/custom-price.util';
import { ChangePriceDialogComponent } from '../change-price-dialog/change-price-dialog.component';
import { extractGeneralPriceInfoFromOrderConfirmation, OrderConfirmationItem } from '../interface';

interface RecurringChargeTotal {
  total: number;
  isStartingPrice: boolean;
  frequency?: GalaxyFrequency;
}

@Component({
  selector: 'app-activation-price',
  templateUrl: './activation-price.component.html',
  styleUrls: ['./activation-price.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivationPriceComponent implements OnDestroy {
  static readonly CENTS_DOLLAR: number = 100;
  @Input() hidePricing: boolean;
  @Input() productsAreInstantBilled: boolean;
  @Input() items: OrderConfirmationItem[];
  @Output()
  orderConfirmationItemEmitter: EventEmitter<OrderConfirmationItem> = new EventEmitter<OrderConfirmationItem>();
  @Output() wholesaleTotal: EventEmitter<number> = new EventEmitter<number>();

  @Input()
  set activationItemSummaries(summaries: OrderConfirmationItem[]) {
    this.summaries = summaries || [];
    this.subscriptions.push(
      this.productOrderFormService.cachedOrder$.subscribe((order) => {
        updateOrderItemsWithCustomPrice(summaries, order);
      }),
    );
    this.fixValuesAndQuantities(summaries);

    this.summaries.forEach((summary) => {
      summary.generalPriceInfo = extractGeneralPriceInfoFromOrderConfirmation(summary);
    });

    this.calculateTotals(summaries);
  }

  summaries: OrderConfirmationItem[];
  total = 0;
  recurringTotals: { [frequency: string]: RecurringChargeTotal } = {};
  currency = 'USD';
  contactSalesForOrder = false;
  minWholesalePrice: { [appId: string]: number } = {};
  private subscriptions: Subscription[] = [];
  constructor(
    private cdr: ChangeDetectorRef,
    private pricePipe: PricePipe,
    public dialog: MatDialog,
    private productOrderFormService: ProductOrderFormStoreService,
  ) {}

  private fixValuesAndQuantities(summaries: OrderConfirmationItem[]): void {
    summaries?.forEach((item) => {
      const appPrice = item.appPrices.pricesForContexts['wholesale'];
      const currValue =
        getPriceValueForEdition(appPrice, item.partnerCurrency, item.editionId || '', true) * item.quantity;

      if (!item.effectiveAppPrice) {
        item.effectiveAppPrice = setInstanceAppPrice(appPrice, item.partnerCurrency, currValue, item.editionId || '');
      }
    });
  }

  private calculateTotals(summaries: OrderConfirmationItem[]): void {
    this.total = getOrderTotal(summaries);
    this.recurringTotals = getRecurringChargeTotals(summaries);
    this.currency = getCurrency(summaries);
    this.contactSalesForOrder = shouldContactSalesForOrder(summaries);
    this.wholesaleTotal.emit(this.total);
  }

  getDiscountDescription(purchaseCost: PurchaseCost): string {
    return 'Discount: ' + this.pricePipe.transform(purchaseCost.discountAmount, Currency[purchaseCost.currency]);
  }

  openChangePriceDialog(item: OrderConfirmationItem): void {
    this.dialog
      .open(ChangePriceDialogComponent, {
        disableClose: true,
        width: '350px',
        data: { item },
      })
      .afterClosed()
      .subscribe((priceInCents: number) => {
        if (priceInCents) {
          const index = this.summaries.findIndex((app) => app.appId === item.appId);
          item.effectiveAppPrice = setInstanceAppPrice(
            item.appPrices.pricesForContexts['wholesale'],
            item.partnerCurrency,
            priceInCents,
            item.editionId || '',
          );

          if (index > -1) {
            this.summaries[index] = {
              appId: item.appId,
              addonId: item.addonId,
              editionId: item.editionId,
              name: item.name,
              icon: item.icon,
              requiresApproval: item.requiresApproval,
              quantity: item.quantity,
              activationMessage: item.activationMessage,
              priceOverride: item.priceOverride,
              editionName: item.editionName,
              appPrices: item.appPrices,
              partnerCurrency: item.partnerCurrency,
              retailTotal: item.retailTotal,
              productPricing: item.productPricing,
              effectiveAppPrice: item.effectiveAppPrice,
              effectiveRetailAppPrice: item.effectiveRetailAppPrice,
              marketCurrency: item.marketCurrency,
            };

            this.summaries[index].generalPriceInfo = extractGeneralPriceInfoFromOrderConfirmation(
              this.summaries[index],
            );

            this.calculateTotals(this.summaries);

            // Update the order confirmation item
            let orderItemToEmit: OrderConfirmationItem;
            for (let i = 0; i < this.items.length; i++) {
              const orderItem = this.items[i];
              if (
                orderItem.appId === item.appId &&
                orderItem.addonId === item.addonId &&
                orderItem.editionId === item.editionId
              ) {
                orderItemToEmit = orderItem;
                break;
              }
            }
            this.orderConfirmationItemEmitter.emit(orderItemToEmit);

            // fire angular change detection
            this.cdr.detectChanges();
          }
        }
      });
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}

// Returns the total price for all the items
export function getOrderTotal(items: OrderConfirmationItem[]): number {
  // note that totalCost can be negative if the item is free.
  // Those should be excluded from the total.
  return !!items && items.length > 0
    ? items.reduce((accumulator, currentValue) => {
        const priceValueExtract = extractGeneralPriceInfoFromOrderConfirmation(currentValue).price || 0;
        const setupFeeValue =
          getSetupFeeForEdition(
            currentValue.effectiveAppPrice,
            currentValue.partnerCurrency,
            currentValue.editionId || '',
          ) * currentValue.quantity;
        const feeValue =
          calculateFeeAmount(
            priceValueExtract,
            getFeesForEdition(
              currentValue.effectiveAppPrice,
              currentValue.partnerCurrency,
              currentValue.editionId || '',
            ) || [],
          ) * currentValue.quantity;
        return priceValueExtract + setupFeeValue + feeValue + accumulator;
      }, 0)
    : 0;
}

// Returns true if all the items in the order require the user to contact sales.
// This prevents the total from showing "Free" when that's not necessarily the case.
export function shouldContactSalesForOrder(items: OrderConfirmationItem[]): boolean {
  if (!items || items.length === 0) {
    return false;
  }
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const appPrice = item.appPrices.pricesForContexts['wholesale'];
    if (appPrice.displayOverride !== PriceDisplayOverride.PRICE_DISPLAY_OVERRIDE_CONTACT_SALES) {
      return false;
    }
  }
  return true;
}

// Gets the currency for the order. Orders assumes the currency will be the
// same across all the products, so it will try to determine a currency from
// the first one.
//
// If no currency is found, USD will be used as the default.
export function getCurrency(items: OrderConfirmationItem[]): string {
  const currency = !!items && items.length > 0 ? items[0].partnerCurrency : 'USD';
  return currency;
}

// Returns a mapping of the billing frequency to the total amount which to
// be charged at the frequency.
export function getRecurringChargeTotals(items: OrderConfirmationItem[]): {
  [frequency: string]: RecurringChargeTotal;
} {
  const totals: { [frequency: string]: RecurringChargeTotal } = {};
  if (!items) {
    return totals;
  }

  items.forEach((item) => {
    const frequency = getFrequencyValueForEdition(item.effectiveAppPrice, item.partnerCurrency, item.editionId || '');

    if (frequency && frequency !== 'one time') {
      // Infer 0 to avoid summing contact sales value (might be -1)
      const price =
        getPriceValueForEdition(item.effectiveAppPrice, item.partnerCurrency, item.editionId || '', true) || 0;

      const feeAmount =
        calculateFeeAmount(
          price,
          getFeesForEdition(item.effectiveAppPrice, item.partnerCurrency, item.editionId || '') || [],
        ) * item.quantity;

      if (price <= 0) {
        return;
      }
      // Don't discount the setup fee, because the price component is already doing it
      if (!totals[frequency]) {
        totals[frequency] = {
          total: price + feeAmount,
          isStartingPrice: item.editionId === getLowestEditionId(item.effectiveAppPrice, item.partnerCurrency),
          frequency: galaxyFrequencyLabelToGalaxyFrequency(frequency),
        };
      } else {
        totals[frequency].total += price + feeAmount;
        totals[frequency].frequency = galaxyFrequencyLabelToGalaxyFrequency(frequency);
      }
    }
  });
  return totals;
}
